import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import PageHeader from "../../components/common/PageHeader";
import AnalysisListItem from "./AnalysisListItem";
import { useXhr } from "hooks/useXhr";
import { useAnalysisListContext } from "hooks/useAnalysisListContext";
import { useAnalysisContext } from "hooks/useAnalysisContext";
import { Button, Spinner } from "react-bootstrap";
import 'react-quill/dist/quill.snow.css';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    rectSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function AnalysisList() {
    //States
    const [item, setItem] = useState({
        photo: null,
        photoEng: null,
        photoFra: null,
        photoGer: null,
        photoDut: null,
        alt: null,
        position: null,
    });
    const [status, setStatus] = useState(false);
    const [isUpdate, setIsUpdate] = useState(null);
    const [isAnalysis, setIsAnalysis] = useState(null);
    const [lang, setLang] = useState(null);
    const [langAnalysis, setLangAnalysis] = useState(null);
    const [isDelete, setIsDelete] = useState(null);
    const [isModal, setIsModal] = useState(false);
    const [showLimitWarning, setShowLimitWarning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({
        field: null,
        note: null
    });
    const [sortableIds, setSortableIds] = useState([]);

    //Hooks
    const { customXhr } = useXhr()
    const { analysisList, dispatch } = useAnalysisListContext();
    const { analysis, dispatch: analysisDispatch } = useAnalysisContext();

    const requiredFields = ['alt', 'position'];
    const requiredFieldsForAnalysis = ['title'];

    const handleOnChange = (e) => {
        const { id, value } = e.target;

        setItem(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleOnUpdateChange = (e) => {
        const { id, value } = e.target;

        setIsUpdate(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleOnAnalysisChange = (e) => {
        const { id, value } = e.target;

        setIsAnalysis(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const generateUniqueFileName = (file) => {
        const timestamp = Date.now();
        const randomNumber = Math.floor(Math.random() * 10000);
        const extension = file.name.split('.').pop();

        return `${timestamp}_${randomNumber}.${extension}`;
    };

    const handlePhotoInput = (e) => {
        const file = e.target.files[0];
        const id = e.target.id;

        const newFileName = generateUniqueFileName(file);
        const newFile = new File([file], newFileName, { type: file.type });

        if (isUpdate) {
            setIsUpdate(prevState => ({
                ...prevState,
                [id]: newFile
            }));
        } else {
            setItem(prevState => ({
                ...prevState,
                [id]: newFile
            }));
        }
    };

    const handleDelete = async (id) => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'analysis_list');
        formData.append('action', 'delete');
        formData.append('id', id);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setStatus(!status);
            setIsDelete(null);
            setIsLoading(false);
        };
    };

    const handleUpdate = async () => {
        for (const field of requiredFields) {
            if (!isUpdate[field] || isUpdate[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'analysis_list');
        formData.append('action', 'update');
        formData.append('id', isUpdate.id);
        formData.append('photo', isUpdate.photo);
        formData.append('photoEng', isUpdate.photoEng);
        formData.append('photoFra', isUpdate.photoFra);
        formData.append('photoGer', isUpdate.photoGer);
        formData.append('photoDut', isUpdate.photoDut);
        formData.append('alt', isUpdate.alt);
        formData.append('position', isUpdate.position);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setIsUpdate(null);
            setLang(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const handleAnalysis = async () => {
        for (const field of requiredFieldsForAnalysis) {
            if (!isAnalysis[field] || isAnalysis[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'analysis');
        formData.append('action', 'update');
        formData.append('id', isAnalysis.id);
        formData.append('title', isAnalysis.title);
        formData.append('titleEng', isAnalysis.titleEng);
        formData.append('titleFra', isAnalysis.titleFra);
        formData.append('titleGer', isAnalysis.titleGer);
        formData.append('titleDut', isAnalysis.titleDut);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setIsAnalysis(null);
            setLangAnalysis(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const handleSubmit = async () => {
        for (const field of requiredFields) {
            if (!item[field] || item[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'analysis_list');
        formData.append('action', 'insert');
        formData.append('photo', item.photo);
        formData.append('photoEng', item.photoEng);
        formData.append('photoFra', item.photoFra);
        formData.append('photoGer', item.photoGer);
        formData.append('photoDut', item.photoDut);
        formData.append('alt', item.alt);
        formData.append('position', item.position);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setItem({
                photo: null,
                photoEng: null,
                photoFra: null,
                photoGer: null,
                photoDut: null,
                alt: null,
                position: null,
            });
            setIsModal(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const getAnalysisList = async () => {
        const formData = new FormData();
        formData.append('module', 'analysis_list');
        formData.append('action', 'list');

        const response = await customXhr(formData);

        if (response) {
            dispatch({ type: 'SET_ANALYSIS_LIST', payload: response });
            setItem(prevState => ({
                ...prevState,
                position: response.length > 0 ? response.length + 1 : 1
            }));
            getAnalysis();
        };
    };

    const getAnalysis = async () => {
        const formData = new FormData();
        formData.append('module', 'analysis');
        formData.append('action', 'list');

        const response = await customXhr(formData);

        if (response) {
            analysisDispatch({ type: 'SET_ANALYSIS', payload: response[0] });
        };
    };

    useEffect(() => {
        getAnalysisList();
    }, [status]);

    useEffect(() => {
        if (analysisList && analysisList.length > 0) {
            setSortableIds(analysisList.map(item => item.id.toString()));
        }
    }, [analysisList]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setSortableIds((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newOrder = arrayMove(items, oldIndex, newIndex);
                updateOrderOnBackend(newOrder);
                return newOrder;
            });
        }
    };

    const updateOrderOnBackend = async (newOrder) => {
        const formData = new FormData();
        formData.append('module', 'analysis_list');
        formData.append('action', 'sort');
        // Sıralamayı id ve yeni position ile gönderiyoruz
        // newOrder: ["3", "1", "2", ...]
        newOrder.forEach((id, index) => {
            formData.append(`order[${index}][id]`, id);
            formData.append(`order[${index}][position]`, index + 1);
        });

        await customXhr(formData);
    };

    return (
        <div className="container-xxl">
            <PageHeader headerTitle="Sektör Analizi" renderRight={() => {
                return <div className="col-auto d-flex w-sm-100">
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsAnalysis(analysis); setLangAnalysis("tur"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/turkey.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Türkçe Başlık
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsAnalysis(analysis); setLangAnalysis("eng"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/united-kingdom.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        İngilizce Başlık
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsAnalysis(analysis); setLangAnalysis("fra"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/france.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Fransızca Başlık
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsAnalysis(analysis); setLangAnalysis("ger"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/germany.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Almanca Başlık
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsAnalysis(analysis); setLangAnalysis("dut"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/netherlands.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Felemenkçe Başlık
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100" onClick={() => {
                        if (analysisList && analysisList.length >= 10) {
                            setShowLimitWarning(true);
                        } else {
                            setIsModal(true);
                        }
                    }}>
                        <i className="icofont-plus-circle me-2 fs-6"></i>
                        Yeni Öğe
                    </button>
                </div>
            }} />
            {analysisList && analysisList.length > 0 ? (
                <div className="row align-items-center">
                    <div className="col-lg-12 col-md-12 flex-column">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={sortableIds}
                                strategy={rectSortingStrategy}
                            >
                                <div className="row g-3 gy-5 py-3 row-deck">
                                    {sortableIds.map((id) => {
                                        const d = analysisList.find(item => item.id.toString() === id);
                                        if (!d) return null;
                                        return (
                                            <SortableItem key={d.id} id={d.id.toString()}>
                                                <AnalysisListItem
                                                    id={d.id}
                                                    photoName={d.photoName}
                                                    photoEngName={d.photoEngName}
                                                    photoFraName={d.photoFraName}
                                                    photoGerName={d.photoGerName}
                                                    photoDutName={d.photoDutName}
                                                    alt={d.alt}
                                                    onClickEdit={() => { setIsUpdate(d); setLang("tur") }}
                                                    onClickEditEng={() => { setIsUpdate(d); setLang("eng") }}
                                                    onClickEditFra={() => { setIsUpdate(d); setLang("fra") }}
                                                    onClickEditGer={() => { setIsUpdate(d); setLang("ger") }}
                                                    onClickEditDut={() => { setIsUpdate(d); setLang("dut") }}
                                                    onClickDelete={() => { setIsDelete(d); }}
                                                />
                                            </SortableItem>
                                        );
                                    })}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </div>
                </div>
            ) : (
                <div className="text-center py-5">
                    <h4 className="fw-bold">Henüz Öğe Eklenmemiş</h4>
                </div>
            )}
            {/* Uyarı Modalı */}
            <Modal
                show={showLimitWarning}
                onHide={() => setShowLimitWarning(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Uyarı</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="form-label">
                            En fazla 10 adet öğe ekleyebilirsiniz. Yeni bir öğe eklemek için mevcut öğelerden birini silmelisiniz.
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowLimitWarning(false)}>
                        Tamam
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={isModal}
                onHide={() => {
                    setIsModal(null);
                    setItem({
                        photo: null,
                        photoEng: null,
                        photoFra: null,
                        photoGer: null,
                        photoDut: null,
                        alt: null,
                        position: null,
                    });
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Yeni Öğe</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="alt" className="form-label">
                            {"Seo Kelimeler "}
                            <span className="text-danger">
                                (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                            </span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="alt"
                            value={item && item.alt}
                            onChange={handleOnChange}
                            style={{ borderColor: `${error.field === "alt" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "alt" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="photo" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photo"
                            onChange={handlePhotoInput}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "tur"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="alt" className="form-label">
                            {"Seo Kelimeler "}
                            <span className="text-danger">
                                (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                            </span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="alt"
                            value={isUpdate && isUpdate.alt}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "alt" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "alt" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="photo" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photo"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label" style={{ color: "red" }}>
                            {"Bu alanın geçerli görsel öğe genişlik boyutu: 3600px"}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "eng"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İngilizce Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="photoEng" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photoEng"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label" style={{ color: "red" }}>
                            {"Bu alanın geçerli görsel öğe genişlik boyutu: 3600px"}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "fra"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Fransızca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="photoFra" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photoFra"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label" style={{ color: "red" }}>
                            {"Bu alanın geçerli görsel öğe genişlik boyutu: 3600px"}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "ger"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Almanca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="photoGer" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photoGer"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label" style={{ color: "red" }}>
                            {"Bu alanın geçerli görsel öğe genişlik boyutu: 3600px"}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "dut"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Felemenkçe Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="photoDut" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photoDut"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label" style={{ color: "red" }}>
                            {"Bu alanın geçerli görsel öğe genişlik boyutu: 3600px"}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langAnalysis === "tur"}
                onHide={() => {
                    setIsAnalysis(null);
                    setLangAnalysis(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Başlık Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="title" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="title"
                            value={isAnalysis && isAnalysis.title}
                            onChange={handleOnAnalysisChange}
                            style={{ borderColor: `${error.field === "title" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "title" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleAnalysis()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langAnalysis === "eng"}
                onHide={() => {
                    setIsAnalysis(null);
                    setLangAnalysis(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İngilizce Başlık Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleEng" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleEng"
                            value={isAnalysis && isAnalysis.titleEng}
                            onChange={handleOnAnalysisChange}
                            style={{ borderColor: `${error.field === "titleEng" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleEng" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleAnalysis()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langAnalysis === "fra"}
                onHide={() => {
                    setIsAnalysis(null);
                    setLangAnalysis(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Fransızca İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleFra" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleFra"
                            value={isAnalysis && isAnalysis.titleFra}
                            onChange={handleOnAnalysisChange}
                            style={{ borderColor: `${error.field === "titleFra" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleFra" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleAnalysis()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langAnalysis === "ger"}
                onHide={() => {
                    setIsAnalysis(null);
                    setLangAnalysis(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Almanca İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleGer" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleGer"
                            value={isAnalysis && isAnalysis.titleGer}
                            onChange={handleOnAnalysisChange}
                            style={{ borderColor: `${error.field === "titleGer" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleGer" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleAnalysis()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langAnalysis === "dut"}
                onHide={() => {
                    setIsAnalysis(null);
                    setLangAnalysis(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Felemenkçe İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleDut" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleDut"
                            value={isAnalysis && isAnalysis.titleDut}
                            onChange={handleOnAnalysisChange}
                            style={{ borderColor: `${error.field === "titleDut" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleDut" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleAnalysis()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                centered
                show={isDelete}
                onHide={() => {
                    setIsDelete(null);
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Uyarı</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="form-label">
                            Bu öğeyi silmek istediğinize emin misiniz?
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        className="me-1 text-white"
                        onClick={() => handleDelete(isDelete.id)}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Siliniyor...
                                </> :
                                <>
                                    <i className="icofont-ui-delete"></i>
                                    &ensp;
                                    Sil
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

function SortableItem({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab"
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="col-12"
        >
            {children}
        </div>
    );
}

export default AnalysisList;